import React, {
  forwardRef,
  Ref,
  useCallback,
  useEffect,
  useImperativeHandle,
  useState,
  useRef,
} from 'react';
import { unstable_batchedUpdates } from 'react-dom';
import { useSelector } from 'react-redux';
import { useParams } from '@reach/router';
import { useScript } from 'hooks';
import { AssetState, RootState } from 'redux/reducers/type';
import ScriptParamVO from 'commons/vo/param/ScriptParamVO';
import { SearchCondition } from 'commons/constants/KeyFlowConstants';
import { ScriptOptionProps } from 'components/ScriptOption/type';
import { useStyles } from 'components/ScriptOption';
import ArrowDropDownIcon from '@material-ui/icons/ArrowDropDown';
import { ClickAwayListener, MenuList, Grow, Paper, Popper, MenuItem } from '@material-ui/core';
import clsx from 'clsx';

const searchKeywordRecordList = [
  '2점슛',
  '3점슛',
  '자유투',
  '리바운드',
  '어시스트',
  '스틸',
  '블록',
  '파울',
  '턴오버',
  '굿디펜스',
  '인터뷰',
  '하이라이트',
  '교체',
];

const DEFAULT_TEAM_COLOR = '#696969';

function ScriptOption(
  {
    isAutoScroll,
    isHideTeamInfo,
    initKeyword,
    onChangeAutoScroll,
    team1SearchKeywordList,
    team2SearchKeywordList,
  }: ScriptOptionProps,
  ref: Ref<{
    onClickPerson: (keyword: string) => void;
    onClickSearchKeyword: (keyword: string) => void;
  }>,
) {
  const classes = useStyles();
  const params = useParams();
  const { onRequestScriptList } = useScript();

  const { selectedQuarter, selectedTeam1, selectedTeam2 } = useSelector(
    ({ asset }: RootState): AssetState => asset,
  );
  const [searchKeywordRecord, setSearchKeywordRecord] = useState<string>('기록명');
  const [searchKeywordPlayer, setSearchKeywordPlayer] = useState<string>('선수명');

  const [isOpenRecordList, setIsOpenRecordList] = useState(false);
  const [isOpenPlayerList, setIsOpenPlayerList] = useState(false);

  const recordListContainerRef = useRef<HTMLDivElement | null>(null);
  const playerListContainerRef = useRef<HTMLDivElement | null>(null);
  const isBatchUpdatingRef = useRef(false);

  const handleCloseRecordList = useCallback(() => {
    setIsOpenRecordList(false);
  }, []);

  const handleClosePlayerList = useCallback(() => {
    setIsOpenPlayerList(false);
  }, []);

  const handleClickSearchKeywordRecord = useCallback((value: string) => {
    setSearchKeywordRecord(value);
    setIsOpenRecordList(false);
  }, []);

  const handleClickSearchKeywordPlayer = useCallback((value: string) => {
    setSearchKeywordPlayer(value);
    setIsOpenPlayerList(false);
  }, []);

  useEffect(() => {
    const handleDocumentMouseDown = (event: MouseEvent) => {
      const targetNode = event.target as Node;
      if (
        isOpenRecordList &&
        recordListContainerRef.current &&
        !recordListContainerRef.current.contains(targetNode)
      ) {
        setIsOpenRecordList(false);
      }
      if (
        isOpenPlayerList &&
        playerListContainerRef.current &&
        !playerListContainerRef.current.contains(targetNode)
      ) {
        setIsOpenPlayerList(false);
      }
    };
    const handleDocumentKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsOpenRecordList(false);
        setIsOpenPlayerList(false);
      }
    };
    document.addEventListener('mousedown', handleDocumentMouseDown);
    document.addEventListener('keydown', handleDocumentKeyDown);
    return () => {
      document.removeEventListener('mousedown', handleDocumentMouseDown);
      document.removeEventListener('keydown', handleDocumentKeyDown);
    };
  }, [isOpenRecordList, isOpenPlayerList]);

  useImperativeHandle(
    ref,
    () => ({
      onClickPerson: (keyword: string) => {
        setSearchKeywordPlayer(keyword);
      },
      onClickSearchKeyword: (keyword: string) => {
        setSearchKeywordRecord(keyword);
      },
    }),
    [params.assetId, selectedQuarter],
  );

  /**
   * {@link src/components/VideoPlayer/VideoPlayer.tsx} 에서
   * 설정한 keydown(단축키) 이벤트와 중복 되는 버그로 인해 추가
   */
  const onKeyDown = useCallback((event: React.KeyboardEvent) => {
    event.nativeEvent.stopImmediatePropagation();
  }, []);

  useEffect(() => {
    if (initKeyword) {
      if (!searchKeywordRecordList || !team1SearchKeywordList || !team2SearchKeywordList) return;

      // 기록명(record)과 선수명(player) 찾기
      const foundRecord = searchKeywordRecordList.find(record => initKeyword.includes(record));
      const foundPlayer = team1SearchKeywordList
        .concat(team2SearchKeywordList)
        .find(player => initKeyword.includes(`${player.teamName} ${player.number} ${player.name}`));

      // React 16에서 여러 state 업데이트를 배치 처리하여 useEffect가 한 번만 실행되도록 함
      isBatchUpdatingRef.current = true;
      unstable_batchedUpdates(() => {
        if (foundRecord) {
          setSearchKeywordRecord(foundRecord);
        }
        if (foundPlayer) {
          setSearchKeywordPlayer(
            [foundPlayer.teamName, foundPlayer.number || -1, foundPlayer.name]
              .filter(Boolean)
              .join(' '),
          );
        }
        // 다음 렌더 사이클에서 플래그 리셋
        setTimeout(() => {
          isBatchUpdatingRef.current = false;
        }, 0);
      });
    }
  }, [initKeyword, searchKeywordRecordList, team1SearchKeywordList, team2SearchKeywordList]);

  useEffect(() => {
    // 배치 업데이트 중이면 실행하지 않음 (배치 업데이트 완료 후 한 번만 실행되도록)
    if (isBatchUpdatingRef.current) {
      return;
    }

    // 선수번호 -1인 경우(undefined일때 -1로 설정) 서버로 요청하는 keyword에서는 지우기
    let keyword = `${searchKeywordRecord} ${searchKeywordPlayer.replace(
      /(^|\s)-1(\s|$)/g,
      ' ',
    )}`.trim();
    keyword = keyword.replace('기록명', '').replace('선수명', '').trim();

    const param = new ScriptParamVO({
      keyword: keyword || undefined,
      retr: 100,
      page: 0,
      sort: [SearchCondition.Sort.IN_POINT, SearchCondition.Sort.OUT_POINT],
      asc: SearchCondition.Asc.ASC,
      videoId: params.assetId,
      inPoint: selectedQuarter ? selectedQuarter.inPoint : undefined,
      outPoint: selectedQuarter ? selectedQuarter.outPoint : undefined,
    });
    onRequestScriptList(param);
  }, [searchKeywordRecord, searchKeywordPlayer, params.assetId, selectedQuarter]);

  return (
    <div className={classes.root}>
      <div className={classes.searchKeywordContainer}>
        <div className={classes.searchKeywordSelectContainer} ref={recordListContainerRef}>
          <button
            className={classes.searchKeywordSelectTrigger}
            onClick={() => {
              setIsOpenRecordList(prev => !prev);
              setIsOpenPlayerList(false);
            }}
          >
            <span className={classes.searchKeywordSelectValue}>
              {searchKeywordRecord ? searchKeywordRecord : '기록명'}
            </span>
            <ArrowDropDownIcon className={classes.searchKeywordSelectArrow} />
          </button>
          <Popper
            open={isOpenRecordList}
            anchorEl={recordListContainerRef.current}
            role={undefined}
            transition
            disablePortal
            style={{ zIndex: 9999, width: '100%' }}
          >
            {({ TransitionProps, placement }) => (
              <Grow
                {...TransitionProps}
                style={{
                  // height: '320px',
                  // overflowY: 'auto',
                  transformOrigin: placement === 'bottom' ? 'center top' : 'center bottom',
                }}
              >
                <Paper>
                  <ClickAwayListener onClickAway={handleCloseRecordList}>
                    <MenuList id="split-button-menu">
                      <MenuItem
                        onClick={() => handleClickSearchKeywordRecord('기록명')}
                        className={classes.defaultItem}
                      >
                        기록명
                      </MenuItem>
                      {searchKeywordRecordList.map((record, index) => (
                        <MenuItem
                          className={clsx(record === searchKeywordRecord && classes.selectedItem)}
                          key={record + index}
                          onClick={() => handleClickSearchKeywordRecord(record)}
                        >
                          {record}
                        </MenuItem>
                      ))}
                    </MenuList>
                  </ClickAwayListener>
                </Paper>
              </Grow>
            )}
          </Popper>
        </div>
        <div className={classes.searchKeywordSelectContainer} ref={playerListContainerRef}>
          <button
            className={clsx(
              classes.searchKeywordSelectTrigger,
              isHideTeamInfo && classes.isHideTeamInfo,
            )}
            disabled={isHideTeamInfo}
            onClick={() => {
              setIsOpenPlayerList(prev => !prev);
              setIsOpenRecordList(false);
            }}
          >
            {isHideTeamInfo ? (
              <div className={classes.isHideTeamInfo}>팀이 존재하지 않습니다.</div>
            ) : (
              <>
                <span className={classes.searchKeywordSelectValue}>
                  {searchKeywordPlayer && searchKeywordPlayer !== '선수명'
                    ? // 선수 번호 뒤에있는 문자열(선수이름)만 추출
                      searchKeywordPlayer.match(/.*?[-]?\d+\s+(.*)$/)?.[1]
                    : '선수명'}
                </span>
                <ArrowDropDownIcon className={classes.searchKeywordSelectArrow} />
              </>
            )}
          </button>

          <Popper
            open={isOpenPlayerList}
            anchorEl={playerListContainerRef.current}
            role={undefined}
            transition
            disablePortal
            style={{ zIndex: 9999, width: '100%' }}
          >
            {({ TransitionProps, placement }) => (
              <Grow
                {...TransitionProps}
                style={{
                  height: '320px',
                  overflowY: 'auto',
                  transformOrigin: placement === 'bottom' ? 'center top' : 'center bottom',
                }}
              >
                <Paper>
                  <ClickAwayListener onClickAway={handleClosePlayerList}>
                    <MenuList id="split-button-menu">
                      <MenuItem
                        onClick={() => handleClickSearchKeywordPlayer('선수명')}
                        className={classes.defaultItem}
                      >
                        선수명
                      </MenuItem>
                      <MenuItem
                        className={classes.teamItem}
                        style={{
                          backgroundColor: selectedTeam1?.teamColor ?? DEFAULT_TEAM_COLOR,
                          color: '#fff',
                          cursor: 'default',
                        }}
                      >
                        {selectedTeam1?.name}
                      </MenuItem>
                      {team1SearchKeywordList.length > 0 ? (
                        team1SearchKeywordList
                          .filter(player => player.name !== null)
                          .map(player => {
                            const isMatch =
                              searchKeywordPlayer ===
                              `${player.teamName} ${player.number} ${player.name}`;

                            return (
                              <MenuItem
                                key={player.id}
                                className={clsx(isMatch && classes.selectedItem)}
                                onClick={() =>
                                  handleClickSearchKeywordPlayer(
                                    [player.teamName, player.number || -1, player.name]
                                      .filter(Boolean)
                                      .join(' '),
                                  )
                                }
                              >
                                {player.name}
                              </MenuItem>
                            );
                          })
                      ) : (
                        <MenuItem className={classes.defaultItem} disabled>
                          데이터가 존재하지 않습니다.
                        </MenuItem>
                      )}
                      <MenuItem
                        className={classes.teamItem}
                        style={{
                          backgroundColor: selectedTeam2?.teamColor ?? DEFAULT_TEAM_COLOR,
                          color: '#fff',
                          cursor: 'default',
                        }}
                      >
                        {selectedTeam2?.name}
                      </MenuItem>
                      {team2SearchKeywordList.length > 0 ? (
                        team2SearchKeywordList
                          .filter(player => player.name !== null)
                          .map(player => {
                            const isMatch =
                              searchKeywordPlayer ===
                              `${player.teamName} ${player.number} ${player.name}`;

                            return (
                              <MenuItem
                                className={clsx(isMatch && classes.selectedItem)}
                                key={player.id}
                                onClick={() =>
                                  handleClickSearchKeywordPlayer(
                                    [player.teamName, player.number || -1, player.name]
                                      .filter(Boolean)
                                      .join(' '),
                                  )
                                }
                              >
                                {player.name}
                              </MenuItem>
                            );
                          })
                      ) : (
                        <MenuItem className={classes.defaultItem} disabled>
                          데이터가 존재하지 않습니다.
                        </MenuItem>
                      )}
                    </MenuList>
                  </ClickAwayListener>
                </Paper>
              </Grow>
            )}
          </Popper>
        </div>
      </div>
    </div>
  );
}

export default forwardRef(ScriptOption);
